import { createPortal } from "preact/compat";
import { useState } from "preact/hooks";

import { useIsDesktop } from "../hooks/useIsDesktop";
import {
  SIGN_OUT_ACTION,
  SIGN_OUT_CONFIRM_BODY,
  SIGN_OUT_CONFIRM_CANCEL,
  SIGN_OUT_CONFIRM_CONFIRM,
  signOutConfirmTitle,
  signOutUnsentWarning,
} from "../lib/authCopy";
import { queueLength } from "../offline/queue";
import { ConfirmDialog } from "./ConfirmDialog";

export interface SignOutButtonProps {
  /** `useMe().logout`, threaded down from `app.tsx` — called only after
   * the confirm is accepted. */
  onSignOut: () => void | Promise<void>;
  /** `"button"` — full-width outlined button (More / Your sign-in).
   * `"link"` — the compact underlined `.logout-btn` look (desktop sidebar
   * footer, where a full-width button would crowd the identity row). */
  variant?: "button" | "link";
}

/**
 * UX review 2026-10 (fix/ux-signin) — the ONE sign-out affordance. The old
 * app-bar "Done for now" button logged people out instantly and read like
 * "I'm done with my chores," so helpers tapped it after a round and lost
 * their session (one of the reasons production shows repeated failed
 * logins). Signing out now always asks first, through the shared
 * `ConfirmDialog` (focus trap, Escape/Back to cancel, focus restore).
 *
 * `destructive` is set for its focus rule, not just its colour: Cancel
 * gets initial focus, so a reflexive second tap or Enter never signs
 * someone out of a shared farm phone they then can't get back into.
 *
 * The dialog is portalled to `document.body`: the desktop sidebar is
 * `position: sticky` (its own stacking context), so a dialog rendered
 * inside it could paint beneath positioned main-content layers.
 */
export function SignOutButton({ onSignOut, variant = "button" }: SignOutButtonProps) {
  const isDesktop = useIsDesktop();
  const [confirming, setConfirming] = useState(false);
  // Unsent offline taps are cleared on sign-out (so the next person on a
  // shared phone can't replay them) — say so before it happens.
  const [unsent, setUnsent] = useState(0);

  function openConfirm() {
    setUnsent(0);
    setConfirming(true);
    void queueLength()
      .then(setUnsent)
      .catch(() => setUnsent(0));
  }

  return (
    <>
      <button
        type="button"
        class={variant === "link" ? "logout-btn" : "secondary-btn sign-out-btn"}
        onClick={openConfirm}
      >
        {SIGN_OUT_ACTION}
      </button>
      {createPortal(
        <ConfirmDialog
          open={confirming}
          title={signOutConfirmTitle(isDesktop)}
          body={unsent > 0 ? signOutUnsentWarning(unsent) : SIGN_OUT_CONFIRM_BODY}
          confirmLabel={SIGN_OUT_CONFIRM_CONFIRM}
          cancelLabel={SIGN_OUT_CONFIRM_CANCEL}
          destructive
          onConfirm={() => {
            setConfirming(false);
            void onSignOut();
          }}
          onCancel={() => setConfirming(false)}
        />,
        document.body,
      )}
    </>
  );
}
